import React, { MouseEventHandler } from 'react';
import Link from '@v2/shared/components/Link';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

export type FooterMenuGroup = {
  /** The column's heading — 'About', 'Legal', 'Policies'. */
  title: string;
  /**
   * `onClick` is here for the Do Not Sell entry, whose click opens CookieYes' preference
   * centre rather than following its href. Every other entry is an ordinary link and
   * leaves it unset.
   */
  items: { label: string; href: string; onClick?: MouseEventHandler<HTMLAnchorElement> }[];
};

type Props = {
  groups: FooterMenuGroup[];
  /**
   * How many rows tall a column may be before the list starts a new one. One — a single
   * column, however long the list — unless a footer asks otherwise.
   */
  rows?: 1 | 2;
};

/**
 * A footer's columns of site links, one `<nav>` per column.
 *
 * MagSpot's and MagZoom's footers draw the same columns from their own lists, which is
 * the whole of what they have in common down there — the band around them, the seal and
 * the card marks are each brand's own. TopMags' footer keeps its own version: it lays
 * each group out as a single row in serif italic, which is a different thing that happens
 * to hold the same kind of links.
 *
 * The paths the brands hand over are site-relative, so every one of them goes through
 * `buildUrl` here rather than at the call site.
 *
 * `rows` bounds a column's height and lets the list run into a second and third column
 * instead, which is what MagSpot's legacy footer did by floating its items and letting
 * them wrap. Only from lg: the columns are as wide as their longest entry, and below that
 * width there is not room for three of them beside the copyright.
 */
export default function FooterMenu({ groups, rows = 1 }: Props) {
  const { buildUrl } = useUrlGenerator();

  return (
    <>
      {groups.map((group) => (
        <nav key={group.title} aria-label={group.title}>
          <h2 className="mb-2 text-base font-bold text-slate-900">{group.title}</h2>
          <ul
            className={cn(
              'm-0 list-none p-0',
              rows === 2 && 'lg:grid lg:auto-cols-max lg:grid-flow-col lg:grid-rows-2 lg:gap-x-8'
            )}
          >
            {group.items.map((item) => (
              <li key={item.href} className="mb-2">
                <Link
                  href={buildUrl(item.href)}
                  className="text-sm text-primary no-underline hover:underline"
                  onClick={item.onClick}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ))}
    </>
  );
}
